import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  TextField,
  Chip,
} from '@mui/material';
import {
  Storage,
  Refresh,
  Visibility,
  Download,
  Code,
} from '@mui/icons-material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface LogFile {
  name: string;
  size: number;
  size_formatted: string;
  modified_at: string;
  path: string;
}

interface LogView {
  filename: string;
  size: number;
  size_formatted: string;
  content: string;
  lines_count: number;
}

const BackendLogs: React.FC = () => {
  const { user } = useAuth();
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<LogFile | null>(null);
  const [logContent, setLogContent] = useState<LogView | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [lines, setLines] = useState<number>(1000);
  const [tailOnly, setTailOnly] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Check if user is admin
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  useEffect(() => {
    if (isPmpAdmin()) {
      fetchLogFiles();
    }
  }, []);

  const fetchLogFiles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/logs');
      setLogFiles(response.data.data);
    } catch (error: any) {
      console.error('Error fetching log files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewLog = async (file: LogFile) => {
    setSelectedFile(file);
    setLoadingContent(true);
    setViewDialogOpen(true);
    
    // Auto-enable tail mode for files larger than 5MB
    const shouldUseTail = tailOnly || file.size > 5 * 1024 * 1024;
    if (shouldUseTail && !tailOnly) {
      setTailOnly(true);
    }
    
    try {
      const params: any = {};
      if (lines) params.lines = lines;
      if (shouldUseTail) params.tail = true;
      
      const response = await api.get(`/logs/view/${file.name}`, { params });
      if (response.data.success) {
        setLogContent(response.data.data);
        setErrorMessage('');
      } else {
        console.error('Error fetching log content:', response.data.message);
        setErrorMessage(response.data.message || 'Failed to load log content');
        setLogContent(null);
      }
    } catch (error: any) {
      console.error('Error fetching log content:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load log content';
      setErrorMessage(errorMsg);
      setLogContent(null);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleDownloadLog = async (filename: string) => {
    try {
      const response = await api.get(`/logs/download/${filename}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading log file:', error);
      alert('Failed to download log file');
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedFile(null);
    setLogContent(null);
    setErrorMessage('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (!isPmpAdmin()) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">You do not have permission to view backend logs.</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Backend Logs
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchLogFiles}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : logFiles.length === 0 ? (
          <Alert severity="info">No log files found.</Alert>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>File Name</strong></TableCell>
                    <TableCell><strong>Size</strong></TableCell>
                    <TableCell><strong>Last Modified</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logFiles.map((file) => (
                    <TableRow key={file.name} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Code />
                          <Typography variant="body2">{file.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={file.size_formatted} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{formatDate(file.modified_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Log">
                          <IconButton
                            size="small"
                            onClick={() => handleViewLog(file)}
                            color="primary"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Log">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadLog(file.name)}
                            color="primary"
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* View Log Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={handleCloseViewDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {selectedFile?.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  type="number"
                  label="Lines"
                  value={lines}
                  onChange={(e) => setLines(parseInt(e.target.value) || 1000)}
                  size="small"
                  sx={{ width: 100 }}
                />
                <Button
                  variant={tailOnly ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setTailOnly(!tailOnly)}
                >
                  Tail Only
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => selectedFile && handleViewLog(selectedFile)}
                  disabled={loadingContent}
                >
                  Reload
                </Button>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {loadingContent ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularProgress />
              </Box>
            ) : errorMessage ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
                {selectedFile && selectedFile.size > 5 * 1024 * 1024 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      This file is large ({selectedFile.size_formatted}). Try enabling "Tail Only" mode or reducing the number of lines.
                    </Typography>
                  </Box>
                )}
              </Alert>
            ) : logContent ? (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                  <Chip label={`Size: ${logContent.size_formatted}`} size="small" />
                  <Chip label={`Lines: ${logContent.lines_count}`} size="small" />
                </Box>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: '#1e1e1e',
                    color: '#d4d4d4',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    maxHeight: '60vh',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {logContent.content}
                </Paper>
              </Box>
            ) : (
              <Alert severity="info">No log content available.</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseViewDialog}>Close</Button>
            {selectedFile && (
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => handleDownloadLog(selectedFile.name)}
              >
                Download
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default BackendLogs;

